import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Pause, Play, Search } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { api, apiError } from "../../lib/api";
import { formatCurrency } from "../../lib/utils";
import ExportCsvButton from "../../components/ExportCsvButton";

const STATUS_TONE = {
  active: "bg-teal-500/10 text-teal-300 border-teal-500/20",
  pending: "bg-amber-500/10 text-amber-300 border-amber-500/20",
  suspended: "bg-red-500/10 text-red-300 border-red-500/20",
};

export default function AdminHospitals() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    const params = filter !== "all" ? { status: filter } : {};
    api.get("/admin/hospitals", { params })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, [filter]);

  const filtered = q.trim()
    ? items.filter((h) => [h.name, h.slug].some((v) => (v || "").toLowerCase().includes(q.toLowerCase())))
    : items;

  const act = async (id, kind) => {
    try {
      await api.post(`/admin/hospitals/${id}/${kind}`);
      toast.success(`Hospital ${kind === "approve" ? "approved" : "suspended"}`);
      refresh();
    } catch (e) { toast.error(apiError(e, "Failed")); }
  };

  return (
    <div className="space-y-6" data-testid="admin-hospitals">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ hospitals</div>
        <h1 className="mt-2 text-3xl font-light tracking-tight">Hospitals</h1>
      </div>

      <div className="rounded-xl border border-mb-border bg-mb-surface p-5 grid sm:grid-cols-[180px_1fr] gap-3">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="hospitals-filter" className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-mb-surface2 border-mb-border text-white">
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-mb-fg2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or slug"
            data-testid="hospitals-search"
            className="pl-9 bg-mb-surface2 border-mb-border text-white h-10 rounded-lg" />
        </div>
        <div className="ml-auto">
          <ExportCsvButton
            endpoint="/api/admin/exports/hospitals.csv"
            filename="hospitals"
            params={{ status: filter !== "all" ? filter : undefined }}
            testid="admin-hospitals-export"
          />
        </div>
      </div>

      <div className="rounded-xl border border-mb-border bg-mb-surface overflow-hidden">
        {loading ? (
          <div className="px-6 py-10 text-sm text-mb-fg2 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-10 text-sm text-mb-fg2 text-center" data-testid="hospitals-empty">No hospitals.</div>
        ) : (
          <ul className="divide-y divide-mb-border">
            {filtered.map((h) => (
              <li key={h.id} data-testid={`admin-hospital-row-${h.id}`}
                className="px-5 py-4 grid grid-cols-1 lg:grid-cols-[1.5fr_0.6fr_0.6fr_0.8fr_auto] gap-3 lg:items-center">
                <div className="min-w-0">
                  <Link to={`/admin/hospitals/${h.id}`} className="text-sm text-white truncate hover:text-teal-300">{h.name}</Link>
                  <div className="text-xs text-mb-fg2 truncate">{h.slug}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded border ${STATUS_TONE[h.status]} self-start`}>{h.status}</span>
                <div className="text-xs text-mb-fg2">{h.doctor_count ?? 0} drs · {h.appointment_count ?? 0} appts</div>
                <div className="text-sm text-teal-300 tabular-nums">{formatCurrency(h.net_revenue ?? 0, { decimals: true })}</div>
                <div className="flex gap-2 justify-start lg:justify-end">
                  {h.status === "pending" && (
                    <Button onClick={() => act(h.id, "approve")} data-testid={`approve-${h.id}`}
                      className="bg-teal-500 hover:bg-teal-400 text-zinc-950 h-9 rounded-lg font-medium"><Check className="h-4 w-4 mr-1" /> Approve</Button>
                  )}
                  {h.status === "active" && (
                    <Button onClick={() => act(h.id, "suspend")} variant="ghost" data-testid={`suspend-${h.id}`}
                      className="hover:bg-red-500/10 text-red-400 h-9"><Pause className="h-4 w-4 mr-1" /> Suspend</Button>
                  )}
                  {h.status === "suspended" && (
                    <Button onClick={() => act(h.id, "approve")} variant="ghost" data-testid={`reactivate-${h.id}`}
                      className="hover:bg-teal-500/10 text-teal-300 h-9"><Play className="h-4 w-4 mr-1" /> Reactivate</Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
